import { ReactElement } from 'react'
import TuneIcon from '@mui/icons-material/Tune'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useEditorViewStore } from '@editorStores'
import { useResultsSelection } from '@editorHooks'
import Results2ElementList, { Results2ListItem } from '../Results2ElementList'

type Props = {
  items: Results2ListItem[]
  selectedId?: string
  selectedIds?: string[]
  onSelect?: (id: string) => boolean | void
  onSelectionChange?: (ids: string[]) => boolean | void
  isLoading?: boolean
  emptyMessage?: string
  onCalculate?: () => void
  /** Cancels in-flight results loading; surfaced as an "Abbrechen" footer button. */
  onAbort?: () => void
  /** When true, shows the "Abbrechen" button next to "Berechnen". */
  showAbort?: boolean
  fokusCamera: boolean
  onFokusCameraChange: (next: boolean) => void
  fokusTransparency: boolean
  onFokusTransparencyChange: (next: boolean) => void
  disableForm?: boolean
}

/**
 * Overview list of the "Schubanker" wall sub-tab: one row per stiffening segment, showing the
 * chosen shear anchor and the decisive utilization at the wall's foot and at its head. Shear
 * anchors carry no comment of their own, so the comment column stays off. Selecting several
 * rows opens the multi-edit pane, which fixes their Schubverbinder at once. The header's tune
 * button opens the per-storey anchor selection panel in the detail area on the right.
 */
const Results2ShearAnchorsList = ({
  items,
  selectedId,
  selectedIds,
  onSelect,
  onSelectionChange,
  isLoading,
  emptyMessage,
  onCalculate,
  onAbort,
  showAbort = false,
  fokusCamera,
  onFokusCameraChange,
  fokusTransparency,
  onFokusTransparencyChange,
  disableForm = false,
}: Props): ReactElement => {
  const selectionOpen = useEditorViewStore(state => state.results.shearAnchorSelectionOpen)
  const setSelectionOpen = useEditorViewStore(state => state.setResultsShearAnchorSelectionOpen)
  const { resetResultsSelection } = useResultsSelection()

  const handleToggleSelectionPane = () => {
    if (!selectionOpen) {
      // The panel renders in the detail area, which the selected segment's detail pane
      // would otherwise occupy.
      resetResultsSelection()
    }
    setSelectionOpen(!selectionOpen)
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="caption" color="text.secondary">
          Wählbare Schubanker je Geschoss einstellen
        </Typography>
        <Tooltip title="Wählbare Schubanker je Geschoss einstellen">
          <IconButton
            size="small"
            color={selectionOpen ? 'primary' : 'default'}
            onClick={handleToggleSelectionPane}
            data-cy="shear-anchor-selection-toggle"
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Results2ElementList
          items={items}
          elementType="standard_rips"
          // No cross section and no η c,90 on this tab: the second column holds the
          // chosen Schubanker, so that is what there is to sort by.
          sortModes={['eta', 'alpha', 'anchor']}
          qsFilterKind="anchor"
          showShearAnchorPositionFilter
          selectedId={selectedId}
          selectedIds={selectedIds}
          onSelect={onSelect}
          onSelectionChange={onSelectionChange}
          multiSelectEnabled
          groupingEnabled={false}
          isLoading={isLoading}
          emptyMessage={emptyMessage}
          showBlockpadButton={false}
          onCalculate={onCalculate}
          onAbort={onAbort}
          showAbort={showAbort}
          showCommentColumn={false}
          qsFullWidth
          hideExportButton
          fokusCamera={fokusCamera}
          onFokusCameraChange={onFokusCameraChange}
          fokusTransparency={fokusTransparency}
          onFokusTransparencyChange={onFokusTransparencyChange}
          disableForm={disableForm}
        />
      </Box>
    </Box>
  )
}

export default Results2ShearAnchorsList
